import React, { useState, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { accountService } from '../../../services/accountService';
import { categoryService } from '../../../services/categoryService';

const FilterPanel = ({ filters, onFilterChange, onResetFilters }) => {
  const [accounts, setAccounts] = useState([]);
  const [parentCategories, setParentCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [selectedParentCategory, setSelectedParentCategory] = useState('');

  useEffect(() => {
    loadAccounts();
    loadParentCategories();
  }, []);

  useEffect(() => {
    if (selectedParentCategory) {
      loadSubcategories(selectedParentCategory);
    } else {
      setSubcategories([]);
    }
  }, [selectedParentCategory]);

  const loadAccounts = async () => {
    try {
      const data = await accountService?.getAll();
      setAccounts(data || []);
    } catch (error) {
      console.error('Error loading accounts:', error);
    }
  };

  const loadParentCategories = async () => {
    try {
      const data = await categoryService?.getParentCategories();
      setParentCategories(data || []);
    } catch (error) {
      console.error('Error loading parent categories:', error);
    }
  };

  const loadSubcategories = async (parentId) => {
    try {
      const data = await categoryService?.getSubcategories(parentId);
      setSubcategories(data || []);
    } catch (error) {
      console.error('Error loading subcategories:', error);
    }
  };

  const accountOptions = [
    { value: '', label: 'All Accounts' },
    ...accounts?.map(acc => ({ value: acc?.id, label: acc?.name }))
  ];

  const typeOptions = [
    { value: '', label: 'All Types' },
    { value: 'income', label: 'Income' },
    { value: 'expense', label: 'Expense' },
    { value: 'transfer_in', label: 'Transfer In' },
    { value: 'transfer_out', label: 'Transfer Out' }
  ];

  const parentCategoryOptions = [
    { value: '', label: 'All Categories' },
    ...parentCategories?.map(cat => ({ value: cat?.id, label: cat?.name }))
  ];

  const subcategoryOptions = [
    { value: '', label: 'All Subcategories' },
    ...subcategories?.map(cat => ({ value: cat?.id, label: cat?.name }))
  ];

  const handleParentCategoryChange = (value) => {
    setSelectedParentCategory(value);
    onFilterChange('category', value);
    onFilterChange('subcategory', ''); // Reset subcategory when parent changes
  };

  const hasActiveFilters = Object.values(filters)?.some(value => value !== '');

  return (
    <div className="bg-card rounded-lg shadow-elevation-2 p-16 md:p-20">
      <div className="flex items-center justify-between mb-16">
        <div className="flex items-center gap-8">
          <Icon name="Filter" size={20} color="var(--color-primary)" />
          <h3 className="text-base md:text-lg font-heading font-semibold text-card-foreground">
            Advanced Filters
          </h3>
        </div>
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            iconPosition="left"
            onClick={onResetFilters}
          >
            Clear All
          </Button>
        )}
      </div>

      {/* Mobile-friendly stacked layout */}
      <div className="space-y-12">
        {/* Search Field - Full Width */}
        <div>
          <label className="block text-sm font-caption font-medium text-foreground mb-8">
            Search
          </label>
          <Input
            type="text"
            placeholder="Search transactions..."
            value={filters?.searchTerm}
            onChange={(e) => onFilterChange('searchTerm', e?.target?.value)}
          />
        </div>

        {/* Date Range - Stack on mobile, side by side on tablet+ */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-12">
          <div>
            <label className="block text-sm font-caption font-medium text-foreground mb-8">
              Date From
            </label>
            <Input
              type="date"
              value={filters?.dateFrom}
              onChange={(e) => onFilterChange('dateFrom', e?.target?.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-caption font-medium text-foreground mb-8">
              Date To
            </label>
            <Input
              type="date"
              value={filters?.dateTo}
              onChange={(e) => onFilterChange('dateTo', e?.target?.value)}
            />
          </div>
        </div>

        {/* Account and Type - Stack on mobile */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-12">
          <div>
            <label className="block text-sm font-caption font-medium text-foreground mb-8">
              Account
            </label>
            <Select
              options={accountOptions}
              value={filters?.account}
              onChange={(value) => onFilterChange('account', value)}
            />
          </div>

          <div>
            <label className="block text-sm font-caption font-medium text-foreground mb-8">
              Type
            </label>
            <Select
              options={typeOptions}
              value={filters?.type}
              onChange={(value) => onFilterChange('type', value)}
            />
          </div>
        </div>

        {/* Category and Subcategory - Stack on mobile */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-12">
          <div>
            <label className="block text-sm font-caption font-medium text-foreground mb-8">
              Category
            </label>
            <Select
              options={parentCategoryOptions}
              value={filters?.category}
              onChange={handleParentCategoryChange}
            />
          </div>

          {selectedParentCategory && subcategories?.length > 0 && (
            <div>
              <label className="block text-sm font-caption font-medium text-foreground mb-8">
                Subcategory (Optional)
              </label>
              <Select
                options={subcategoryOptions}
                value={filters?.subcategory || ''}
                onChange={(value) => onFilterChange('subcategory', value)}
              />
            </div>
          )}
        </div>

        {/* Amount Range */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-12">
          <div>
            <label className="block text-sm font-caption font-medium text-foreground mb-8">
              Min Amount
            </label>
            <Input
              type="number"
              placeholder="0.00"
              value={filters?.amountMin}
              onChange={(e) => onFilterChange('amountMin', e?.target?.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-caption font-medium text-foreground mb-8">
              Max Amount
            </label>
            <Input
              type="number"
              placeholder="0.00"
              value={filters?.amountMax}
              onChange={(e) => onFilterChange('amountMax', e?.target?.value)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterPanel;